import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTheme } from 'styled-components';
import { FovTypography, MESSAGE_TYPES } from 'components';
import { FovPopover } from 'components/generic';
import { DocsOutlined, EyeOutlined } from 'components/icons';
import constants from 'constants/index';
import { TableThumbnail } from 'pages/ArchVisionAdminDashboard/styles';
import { getModelsAdmin } from 'store/actions/admin';
import { addMessage } from 'store/actions/app';
import {
    NameContainer,
    ThumbnailTitleContainer,
    CategoryHeaderContainer, DateTimeContainer,
    CategoryHeaderLabel,
    CategoryFilterWrapper, CategoryFilterSelect,
    RowMenuCopyButton,
} from './styles';
import { RowMenuButton, RowMenuItem, TruncatedCell } from 'pages/ArchVisionAdminDashboard/elements/Jobs/styles';

const { CONTEXT_VALUES } = constants;

const copyToClipboard = async ({ value, label, dispatch, setOpenMenuGuid }) => {
    setOpenMenuGuid(null);

    try {
        await navigator.clipboard.writeText(value);
        dispatch(addMessage([ { message: `${label} copied`, config: { type: MESSAGE_TYPES.success, timer: 3 } } ]));
    } catch (error) {
        console.error(error);
        dispatch(addMessage([ { message: `Failed to copy ${label}`, config: { type: MESSAGE_TYPES.error } } ]));
    }
};

const CreatorColumnHeader = withTheme(({ theme, searchParams, setSearchParams, dispatch, token }) => {
    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);
    const creators = useSelector(state => state.adminData.creators);

    return (
        <CategoryHeaderContainer>
            <CategoryHeaderLabel theme={theme} $selectedTheme={selectedTheme}>Creator</CategoryHeaderLabel>
            <CategoryFilterWrapper theme={theme} $selectedTheme={selectedTheme}>
                <CategoryFilterSelect
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    value={searchParams?.get('creatorId') || ''}
                    onChange={e => {
                        const newVal = e.target.value;
                        if (newVal) {
                            searchParams.set('creatorId', newVal);
                        } else {
                            searchParams.delete('creatorId');
                        }
                        searchParams.set('page', 1);
                        setSearchParams(searchParams);
                        dispatch(getModelsAdmin(token));
                    }}
                >
                    <option value=''>All</option>
                    {creators?.filter(creator => creator.modelCount > 0).map(creator => (
                        <option key={creator.creator_id} value={creator.creator_id}>
                            {creator.display_name}
                        </option>
                    ))}
                </CategoryFilterSelect>
            </CategoryFilterWrapper>
        </CategoryHeaderContainer>
    );
});

const CategoryColumnHeader = withTheme(({ theme, searchParams, setSearchParams, dispatch, token }) => {
    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);
    const rpcCategories = useSelector(state => state.rpc.rpcCategories);

    return (
        <CategoryHeaderContainer>
            <CategoryHeaderLabel theme={theme} $selectedTheme={selectedTheme}>Category</CategoryHeaderLabel>
            <CategoryFilterWrapper theme={theme} $selectedTheme={selectedTheme}>
                <CategoryFilterSelect
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    value={searchParams?.get('category') || ''}
                    onChange={e => {
                        const newVal = e.target.value;
                        if (newVal) {
                            searchParams.set('category', newVal);
                        } else {
                            searchParams.delete('category');
                        }
                        searchParams.set('page', 1);
                        setSearchParams(searchParams);
                        dispatch(getModelsAdmin(token));
                    }}
                >
                    <option value=''>All</option>
                    {rpcCategories.map(cat => (
                        <option key={cat.title} value={cat.title}>{cat.title}</option>
                    ))}
                </CategoryFilterSelect>
            </CategoryFilterWrapper>
        </CategoryHeaderContainer>
    );
});

export const listColumns = (
    theme,
    selectedTheme,
    searchParams,
    setSearchParams,
    dispatch,
    token,
    openMenuGuid,
    setOpenMenuGuid,
) => ([
    {
        title: 'Title',
        key: 'title',
        width: 380,
        alignContent: 'left',
        render: (text, model) => {
            const { previewURL, preview256URL, preview512URL } = model;
            return (
                <ThumbnailTitleContainer>
                    <TableThumbnail src={preview256URL || preview512URL || previewURL} />
                    <div>{model.title}</div>
                </ThumbnailTitleContainer>
            );
        },
    },
    {
        title: <CategoryColumnHeader
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            dispatch={dispatch}
            token={token}
        />,
        key: 'category',
        width: 220,
        render: (text, model) => (
            <NameContainer>
                <FovTypography fontSize={16}>{model.category}</FovTypography>
                {model.subcategory && <FovTypography fontSize={12}>└ {model.subcategory}</FovTypography>}
            </NameContainer>
        ),
    },
    {
        title: <CreatorColumnHeader
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            dispatch={dispatch}
            token={token}
        />,
        key: 'creator',
        width: 300,
        render: (text, model) => {
            const label = model.creator || model.user?.email;
            return label ? <FovTypography>{label}</FovTypography> : '';
        },
    },
    {
        title: 'Job Type',
        key: 'job_type',
        width: 185,
        alignContent: 'left',
        render: (text, model) => (
            <TruncatedCell title={model.job_type}>
                {model.job_type ?? '-'}
            </TruncatedCell>
        ),
    },
    {
        title: 'Created',
        key: 'created_at',
        width: 120,
        render: (text, model) => (
            <DateTimeContainer>
                <FovTypography>
                    {model.created_at ? new Date(model.created_at).toLocaleDateString(
                        'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                </FovTypography>

                <FovTypography>
                    {model.created_at ? new Date(model.created_at).toLocaleTimeString(
                        'en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) : ''}
                </FovTypography>
            </DateTimeContainer>
        ),
    },
    {
        title: 'Updated',
        key: 'updated_at',
        width: 120,
        render: (text, model) => (
            <DateTimeContainer>
                <FovTypography>
                    {model.updated_at ? new Date(model.updated_at).toLocaleDateString(
                        'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                </FovTypography>

                <FovTypography>
                    {model.updated_at ? new Date(model.updated_at).toLocaleTimeString(
                        'en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) : ''}
                </FovTypography>
            </DateTimeContainer>
        ),
    },
    {
        title: '',
        key: 'actions',
        width: 60,
        alignContent: 'center',
        render: (text, model) => (
            <FovPopover
                noArrow
                position='bottom'
                top={32}
                right={8}
                padding={4}
                isVisible={openMenuGuid === model.rpc_guid}
                callback={() => setOpenMenuGuid(
                    current => (current === model.rpc_guid ? null : model.rpc_guid),
                )}
                content={(
                    <Fragment>
                        <RowMenuItem
                            as={Link}
                            to={`/preview-model?rpc_guid=${model.rpc_guid}&context=${CONTEXT_VALUES.ADMIN}`}
                            $selectedTheme={selectedTheme}
                        >
                            <EyeOutlined width={16} height={16} />
                            Preview
                        </RowMenuItem>

                        <RowMenuCopyButton
                            $selectedTheme={selectedTheme}
                            onClick={() => copyToClipboard({
                                value: model.rpc_guid,
                                label: 'RPC GUID',
                                dispatch,
                                setOpenMenuGuid,
                            })}
                        >
                            <DocsOutlined width={16} height={16} />
                            Copy RPC GUID
                        </RowMenuCopyButton>

                        <RowMenuCopyButton
                            $selectedTheme={selectedTheme}
                            $disabled={!model.job_guid}
                            disabled={!model.job_guid}
                            title={model.job_guid ? undefined : 'This model has no job'}
                            onClick={() => copyToClipboard({
                                value: model.job_guid,
                                label: 'Job GUID',
                                dispatch,
                                setOpenMenuGuid,
                            })}
                        >
                            <DocsOutlined width={16} height={16} />
                            Copy Job GUID
                        </RowMenuCopyButton>
                    </Fragment>
                )}
            >
                <RowMenuButton type='button' $selectedTheme={selectedTheme}>
                    <span aria-hidden='true'>⋮</span>
                </RowMenuButton>
            </FovPopover>
        ),
    },
]);
